import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { PopupNames } from 'core';
import { IGroupDetail, usePopupStateOperations } from 'core/hooks';
import { IGroup, PublishedLmsType } from 'components/types/common';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../_detailShared/detail.styled';
import { SyncMenu } from './parts/SyncMenu';
import { AddStudentToGroupInlinePopup } from './parts/AddStudentToGroupInlinePopup';

interface IHeaderBarProps {
  group: IGroupDetail;
  /** Called after a sync / mutation from the HeaderBar — caller refetches. */
  onSynced: () => void;
}

/**
 * Page header for `/settings/groups/:id/details` — mirrors the
 * StudentDetail HeaderBar. Identity strip (name + course/dates/price)
 * on the left, status pills under it, primary actions on the right.
 *
 * Actions wired in Phase A:
 *   - **Edit group** → `PopupNames.editGroupPopup` (existing flow).
 *   - **Archive** → `PopupNames.deleteGroup` (gated by `deleteAvailable`).
 *   - **Toggle LMS publication** → `addGroupInLMS` / `removeGroupFromLMS`
 *     popups (existing). Needs a minimal `IGroup` shape — we adapt
 *     from `IGroupDetail` rather than re-fetching.
 *   - **+ Студент** → opens `AddStudentToGroupInlinePopup` which uses
 *     `appendStudentToGroup` (backend TZ §G8 shipped) — async-search
 *     the existing student, the agreement lands in this group with
 *     its currency prefilled.
 *   - **Sync menu (Fakturownia / PandaDoc / Zoom)** → see `SyncMenu`
 *     part. Fakturownia/PandaDoc reuse the global sync hooks; Zoom is
 *     a stub gated on backend TZ G9.
 */
export const HeaderBar = memo(({ group, onSynced }: IHeaderBarProps) => {
  const { t } = useTranslation();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const subtitle = useMemo(() => {
    const parts: string[] = [];
    if (group.course?.name) parts.push(group.course.name);
    if (group.startDate)
      parts.push(
        `${t('pages.groupDetail.startDate')}: ${moment(group.startDate).format(
          'DD.MM.YYYY',
        )}`,
      );
    if (group.price != null) {
      const cur = group.currency ? ` ${group.currency}` : '';
      parts.push(`${group.price}${cur}`);
    }
    return parts.join(' · ');
  }, [group.course?.name, group.startDate, group.price, group.currency, t]);

  /**
   * Adapt `IGroupDetail` → the legacy `IGroup` shape expected by the
   * LMS popups. Most fields are stringified counts (display props),
   * so we coerce from numeric counts here. `lessonDays` defaults to
   * `[]` — the popups only use it for display, not validation.
   */
  const lmsGroupShape: IGroup = useMemo(
    () => ({
      id: group.id,
      group: group.name,
      startDate: group.startDate || '',
      publishedLms: Boolean(group.publishedLms),
      publishedLmsForTeacher: Boolean(group.publishedLmsForTeacher),
      publishedLmsForMethodist: Boolean(group.publishedLmsForMethodist),
      publishedLmsType: PublishedLmsType.student,
      numberOfLessons: String(group.lessonsCount ?? 0),
      numberOfStudents: String(group.studentsCount ?? 0),
      lessonDays: [],
      agreementsSum: String(group.contributionsSum ?? ''),
    }),
    [group],
  );

  const isLmsPublished = Boolean(
    group.publishedLms ||
      group.publishedLmsForTeacher ||
      group.publishedLmsForMethodist,
  );

  const [appendOpen, setAppendOpen] = useState(false);
  const onAddStudent = useCallback(() => setAppendOpen(true), []);

  const onEdit = useCallback(() => {
    showPopup(PopupNames.editGroupPopup, group.id);
  }, [showPopup, group.id]);

  const onArchive = useCallback(() => {
    showPopup(PopupNames.deleteGroup, { groupId: group.id });
  }, [showPopup, group.id]);

  const onToggleLms = useCallback(() => {
    showPopup(
      isLmsPublished ? PopupNames.removeGroupFromLMS : PopupNames.addGroupInLMS,
      lmsGroupShape,
    );
  }, [showPopup, isLmsPublished, lmsGroupShape]);

  return (
    <>
      <S.HeaderRow>
        <S.PageTitle>{group.name}</S.PageTitle>
        <L.HeaderActions>
          <L.PrimaryActionButton
            type="button"
            tone="primary"
            onClick={onAddStudent}
          >
            {t('pages.groupDetail.actions.addStudent')}
          </L.PrimaryActionButton>
          <L.PrimaryActionButton type="button" onClick={onEdit}>
            {t('pages.groupDetail.actions.edit')}
          </L.PrimaryActionButton>
          <SyncMenu groupId={group.id} onSynced={onSynced} />
          <L.PrimaryActionButton type="button" onClick={onToggleLms}>
            {isLmsPublished
              ? t('pages.groupDetail.actions.unpublishLms')
              : t('pages.groupDetail.actions.publishLms')}
          </L.PrimaryActionButton>
          {group.deleteAvailable ? (
            <L.PrimaryActionButton type="button" onClick={onArchive}>
              {t('pages.groupDetail.actions.archive')}
            </L.PrimaryActionButton>
          ) : null}
        </L.HeaderActions>
      </S.HeaderRow>
      <S.SubTitle>{subtitle || '—'}</S.SubTitle>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          marginTop: 6,
          marginBottom: 4,
        }}
      >
        <L.Tag tone={group.statusFinished ? 'neutral' : 'ok'}>
          {group.statusFinished
            ? t('pages.groupDetail.statusPill.finished')
            : t('pages.groupDetail.statusPill.active')}
        </L.Tag>
        {group.isFree ? (
          <L.Tag tone="warn">{t('pages.groupDetail.statusPill.free')}</L.Tag>
        ) : null}
        {isLmsPublished ? (
          <L.Tag tone="ok">
            {t('pages.groupDetail.statusPill.lmsPublished')}
          </L.Tag>
        ) : null}
        {group.deleteAvailable ? null : (
          <L.Tag tone="neutral">
            {t('pages.groupDetail.statusPill.archived')}
          </L.Tag>
        )}
      </div>
      {appendOpen ? (
        <AddStudentToGroupInlinePopup
          group={group}
          onClose={() => setAppendOpen(false)}
          onAdded={onSynced}
        />
      ) : null}
    </>
  );
});
